// Hit testing against the retained scene.
//
// This is the part the SVG renderer gave away for free and the part the map most
// depends on: right-clicking one wedge of a role's spinner has to resolve to that
// wedge, because the `data-props` on it is what turns the menu into a Kibana
// query for that log level. So the test is against real geometry, not bounding
// boxes, and it follows SVG's `visiblePainted` rule — only painted fill and
// painted stroke respond.

import { flattenPath } from "./path";
import { COLOUR_NONE } from "../scene/style";
import { num } from "./geometry";
import { fontKeyFor, fontMetrics, baselineOffset, layoutText } from "./text";

// Strokes are given a little extra reach so that hairlines stay clickable, in the
// spirit of the browser's own tolerance for thin geometry.
const STROKE_TOLERANCE = 1.5;

export function hitTestNode(node, x, y, style) {
  const hasFill = style.fill !== COLOUR_NONE;
  const hasStroke = style.stroke !== COLOUR_NONE && style.strokeWidth > 0;
  if (!hasFill && !hasStroke) return false;

  const reach = hasStroke ? style.strokeWidth / 2 + STROKE_TOLERANCE : 0;
  const attrs = node.attrs;

  switch (node.tagName) {
    case "circle": {
      const cx = num(attrs.cx, 0);
      const cy = num(attrs.cy, 0);
      const r = num(attrs.r, 0);
      const d = Math.hypot(x - cx, y - cy);
      if (hasFill && d <= r) return true;
      return hasStroke && Math.abs(d - r) <= reach;
    }
    case "rect":
    case "image": {
      const rx = num(attrs.x, 0);
      const ry = num(attrs.y, 0);
      const w = num(attrs.width, 0);
      const h = num(attrs.height, 0);
      if (w <= 0 || h <= 0) return false;
      // An <image> paints its whole box regardless of fill.
      if (node.tagName === "image")
        return x >= rx && x <= rx + w && y >= ry && y <= ry + h;
      if (hasFill && x >= rx && x <= rx + w && y >= ry && y <= ry + h)
        return true;
      if (!hasStroke) return false;
      const inset =
        x >= rx + reach &&
        x <= rx + w - reach &&
        y >= ry + reach &&
        y <= ry + h - reach;
      return (
        !inset &&
        x >= rx - reach &&
        x <= rx + w + reach &&
        y >= ry - reach &&
        y <= ry + h + reach
      );
    }
    case "line": {
      if (!hasStroke) return false;
      return (
        distanceToSegment(
          x,
          y,
          num(attrs.x1, 0),
          num(attrs.y1, 0),
          num(attrs.x2, 0),
          num(attrs.y2, 0)
        ) <= reach
      );
    }
    case "path": {
      const subpaths = flattenPath(attrs.d);
      if (hasFill) {
        for (let i = 0; i < subpaths.length; i++)
          if (pointInPolygon(x, y, subpaths[i].points)) return true;
      }
      if (hasStroke) {
        for (let i = 0; i < subpaths.length; i++)
          if (nearPolyline(x, y, subpaths[i].points, subpaths[i].closed, reach))
            return true;
      }
      return false;
    }
    case "text":
      return hitTestText(node, x, y, style);
    default:
      return false;
  }
}

// Text responds over its layout box rather than the glyph outlines, which is
// close enough to what a browser does for the small labels on the map and avoids
// needing the rasterised coverage during a hit test.
function hitTestText(node, x, y, style) {
  const content = node.textContent;
  if (!content) return false;

  const size = style.fontSize;
  if (!(size > 0)) return false;

  const key = fontKeyFor(style);
  const metrics = fontMetrics(key);
  const layout = layoutText(content, key);
  const spacing = style.letterSpacing || 0;
  const advance = layout.width * size + spacing * layout.clusters.length;

  let left = num(node.attrs.x, 0);
  if (style.textAnchor === "middle") left -= advance / 2;
  else if (style.textAnchor === "end") left -= advance;

  const baseline =
    num(node.attrs.y, 0) +
    baselineOffset(style.dominantBaseline, metrics) * size;

  return (
    x >= left &&
    x <= left + advance &&
    y >= baseline - metrics.ascent * size &&
    y <= baseline + metrics.descent * size
  );
}

function pointInPolygon(x, y, points) {
  let inside = false;
  const n = points.length;
  for (let i = 0, j = n - 2; i < n; j = i, i += 2) {
    const xi = points[i];
    const yi = points[i + 1];
    const xj = points[j];
    const yj = points[j + 1];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)
      inside = !inside;
  }
  return inside;
}

function nearPolyline(x, y, points, closed, reach) {
  for (let i = 0; i + 3 < points.length; i += 2) {
    if (
      distanceToSegment(
        x,
        y,
        points[i],
        points[i + 1],
        points[i + 2],
        points[i + 3]
      ) <= reach
    )
      return true;
  }
  if (closed && points.length >= 4) {
    return (
      distanceToSegment(
        x,
        y,
        points[points.length - 2],
        points[points.length - 1],
        points[0],
        points[1]
      ) <= reach
    );
  }
  return false;
}

function distanceToSegment(px, py, ax, ay, bx, by) {
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared < 1e-12) return Math.hypot(px - ax, py - ay);

  let t = ((px - ax) * dx + (py - ay) * dy) / lengthSquared;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;

  return Math.hypot(px - (ax + dx * t), py - (ay + dy * t));
}

// ---------------------------------------------------------------------------
// Synthetic events
// ---------------------------------------------------------------------------

// The scene's own event object. It carries what the map's handlers read and
// forwards `preventDefault` to the real event so that suppressing the browser
// context menu still works.
export class SceneEvent {
  constructor(type, target, native) {
    this.type = type;
    this.target = target;
    this.currentTarget = target;
    this.native = native;
    this.clientX = native ? native.clientX : 0;
    this.clientY = native ? native.clientY : 0;
    this.button = native ? native.button : 0;
    this.propagationStopped = false;
    this.defaultPrevented = false;
  }

  preventDefault() {
    this.defaultPrevented = true;
    if (this.native) this.native.preventDefault();
  }

  stopPropagation() {
    this.propagationStopped = true;
  }

  stopImmediatePropagation() {
    this.propagationStopped = true;
  }
}

// Bubbles from the target to the root, as a DOM event would.
export function dispatch(type, target, native) {
  const event = new SceneEvent(type, target, native);

  let node = target;
  while (node) {
    const listeners = node.listenersFor(type);
    if (listeners && listeners.length) {
      event.currentTarget = node;
      // Copied because a handler may add or remove listeners while running.
      const snapshot = listeners.slice();
      for (let i = 0; i < snapshot.length; i++) {
        try {
          snapshot[i].call(node, event);
        } catch (e) {
          console.error(e);
        }
      }
      if (event.propagationStopped) break;
    }
    node = node.parentNode;
  }

  return event;
}

// mouseenter and mouseleave do not bubble: each is delivered only to the node it
// is registered on, when the pointer crosses into or out of its subtree.
export function dispatchDirect(type, node, native) {
  const listeners = node.listenersFor(type);
  if (!listeners || !listeners.length) return;

  const event = new SceneEvent(type, node, native);
  const snapshot = listeners.slice();
  for (let i = 0; i < snapshot.length; i++) {
    try {
      snapshot[i].call(node, event);
    } catch (e) {
      console.error(e);
    }
  }
}
